import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  TextField,
  Autocomplete,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { Clear, Print as PrintIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { connect } from "react-redux";
import debounce from "lodash.debounce";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import PrintsSearch3 from "../../../components/PrintSearch3";
import CSVLinkWrapper from "../../../components/CSVLinkWrapper";
import PATClearButton from "../../../components/Buttons/PATClearButton";
import FilterToolbar from "../../../components/FilterToolbar";
import { areAllPropsEmpty, renderCodeOption } from "../../../utils/functions";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import FilterButtons from "../../../components/Buttons/FilterButtons";
import { screenNames } from "../../../utils/constants";
import SelectFilterDialog from "../../../components/SelectFilterDialog";
import SaveFilterDialog from "../../../components/SaveFilterDialog";
import { formatSaveDateFrom, formatSaveDateTo } from "../../../reducers/TimeReducer";

const headers = [
  { key: "courseName", label: "Course Name" },
  { key: "courseBeginDate", label: "Begin Date", format: "date" },
  { key: "courseEndDate", label: "End Date", format: "date" },
  { key: "location", label: "Location" },
  { key: "hours", label: "Hours" },
  { key: "category", label: "Category" },
  { key: "instructor", label: "Instructor" },
];

const columnsForPrint = headers.map((h) => ({
  field: h.key,
  headerName: h.label,
  editable: false,
  flex: 1,
  ...h,
}));

const CourseTableToolbar = ({
  courses,
  keyboard,
  onAddClick,
  onFilterChange,
  filters,
  resetFilters,
  locationOptions,
  categoryOptions,
  tagTypeOptions,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const [printFlag, setPrintFlag] = useState(false);
  const [kbdKey, setKbdKey] = useState(null);
  const [localFilters, setLocalFilters] = useState(filters);
  const [openSelectFilterDialog, setOpenSelectFilterDialog] = useState(false);
  const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false);
  
  const isFilterEmpty = areAllPropsEmpty(localFilters);

  const coursePermission = getAccessPermission("training", "Courses", "any");
  const canAddCourse = !!coursePermission?.Create;

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilterChange = useCallback(
    debounce((newFilters) => {
      onFilterChange(newFilters);
    }, 500),
    [onFilterChange]
  );

  const handleFilterChange = useCallback(
    (field, value) => {
      const updatedFilters = { ...localFilters, [field]: value };
      setLocalFilters(updatedFilters);
      debouncedFilterChange(updatedFilters);
    },
    [debouncedFilterChange, localFilters]
  );

  useEffect(() => {
    const subscription = keyboard.subject.subscribe((key) => {
      const validKeys = ["F5", "Ctrl+KeyP", "Ctrl+KeyL"];
      if (validKeys.includes(key)) setKbdKey(key);
    });
    return () => subscription.unsubscribe();
  }, [keyboard]);

  const handlePrintClick = () => {
    setPrintFlag(true);
    setTimeout(() => setPrintFlag(false), 1000);
  };

  const renderFilterFields = (fields) =>
    fields.map(
      ({ type, label, field, options, xs = 2, multiselect = true }) => (
        <Grid item xs={isSmall ? 12 : xs} key={field}>
          {type === "text" ? (
            <TextField
              label={label}
              value={localFilters[field] || ""}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          ) : type === "date" ? (
            <DatePicker
              label={label}
              value={localFilters[field] ? moment(localFilters[field]) : null}
              onChange={(date) =>
                handleFilterChange(field, date ? field === "courseBeginDate" ? formatSaveDateFrom(date) : formatSaveDateTo(date) : null)
              }
              minDate={
                field === "courseEndDate" && localFilters.courseBeginDate
                  ? moment(localFilters.courseBeginDate)
                  : undefined
              }
              maxDate={
                field === "courseBeginDate" && localFilters.courseEndDate
                  ? moment(localFilters.courseEndDate)
                  : undefined
              }
              clearable
              clearText="Clear"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {localFilters[field] && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleFilterChange(field, null)}
                              size="small"
                              edge="end"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            <Autocomplete
              multiple={multiselect}
              options={options || []}
              value={
                multiselect
                  ? localFilters[field] || []
                  : localFilters[field] || null
              }
              onChange={(_, value) => handleFilterChange(field, value)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(o, v) => o?.value === v?.value}
              renderOption={renderCodeOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  variant="outlined"
                  size="small"
                />
              )}
              fullWidth
              sx={
                multiselect
                  ? {
                      "& .MuiAutocomplete-inputRoot": {
                        flexWrap: "nowrap",
                        maxHeight: "40px",
                      },
                      "& .MuiAutocomplete-tag": {
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        alignSelf: "flex-start",
                      },
                    }
                  : {}
              }
            />
          )}
        </Grid>
      )
    );

  const renderOptions = () => {
    return (
      <>
        {isSmall ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid item>
              <CSVLinkWrapper
                data={courses}
                headers={headers}
                filename="courses.csv"
              />
            </Grid>
            <Grid item>
              <IconButton
                onClick={handlePrintClick}
                color="primary"
                disabled={!courses?.length}
              >
                <PrintIcon />
              </IconButton>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              flexDirection: "column",
            }}
          >
            <Grid item>
              <CSVLinkWrapper
                data={courses}
                headers={headers}
                filename="courses.csv"
              />
            </Grid>
            <Grid item>
              <IconButton
                onClick={handlePrintClick}
                color="primary"
                disabled={!courses?.length}
              >
                <PrintIcon />
              </IconButton>
            </Grid>
          </Box>
        )}
      </>
    );
  };

  const renderAddButton = () => {
    return (
      <>
        {canAddCourse && (
          <Grid item>
            <PATAddButton tooltipText="Add Course" onClick={onAddClick} />
          </Grid>
        )}
      </>
    );
  };

  const renderClearButton = () => {
    return (
      <>
        <Grid item>
          <Grid item>
            <PATClearButton title="Clear Filters" onClick={resetFilters} />
          </Grid>
        </Grid>
      </>
    );
  };

  const filterFields1 = [
    { type: "date", label: "Begin Date", field: "courseBeginDate", xs: 2.4 },
    { type: "date", label: "End Date", field: "courseEndDate", xs: 2.4 },
    { type: "text", label: "Course Title", field: "courseName", xs: 2.4 },
    {
      type: "autocomplete",
      label: "Location",
      field: "location",
      xs: 2.4,
      options: locationOptions,
      multiselect: false,
    },
    {
      type: "text",
      label: "Instructor/Course Coordinator",
      field: "instructor",
      xs: 2.4,
    },
    {
      type: "autocomplete",
      label: "Category",
      field: "category",
      xs: 2.9,
      options: categoryOptions,
      multiselect: false,
    },
    {
      type: "autocomplete",
      label: "Tags",
      field: "tagTypes",
      options: tagTypeOptions,
      xs: 2.9,
    },
    { type: "text", label: "Hours", field: "hours", xs: 2.9 },
    { type: "text", label: "Course #", field: "courseNumber", xs: 2.9 },
  ];

  return (
    <>
      <Typography sx={{ fontSize: 20 }}>Courses</Typography>
      <FilterToolbar
        isSmall={isSmall}
        canCreate={canAddCourse}
        leftSection={
          !isSmall && (
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                borderRadius: "10px",
                padding: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                marginLeft: 1,
              }}
            >
              {renderAddButton()}
            </Box>
          )
        }
        middleSection={
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.primary.main}`,
              borderRadius: "10px",
              padding: 2,
              maxHeight: { xs: "25vh", md: "40vh" },
              overflowY: "auto",
              marginLeft: canAddCourse ? "" : 1.5,
            }}
          >
            <Grid container spacing={1}>
              {renderFilterFields([...filterFields1])}
              {renderClearButton()}
              <FilterButtons
                onSelectFilterClick={() => setOpenSelectFilterDialog(true)}
                onSaveFilterClick={() => setOpenSaveFilterDialog(true)}
                isFilterEmpty={isFilterEmpty}
              />
            </Grid>
          </Box>
        }
        rightSection={
          !isSmall && (
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                borderRadius: "10px",
                padding: 2,
                display: "flex",
                height: "100%",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                justifyContent: "center",
                marginRight: 1,
              }}
            >
              {renderOptions()}
            </Box>
          )
        }
        combinedActions={
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {renderAddButton()}
            {renderOptions()}
          </Box>
        }
        leftGridProps={{ xs: 12, md: 1 }}
        middleGridProps={{ xs: 12, md: 10 }}
        rightGridProps={{ xs: 12, md: 1 }}
      />

      <PrintsSearch3
        title="Courses"
        data={courses}
        cols={columnsForPrint}
        kbdKey={kbdKey}
        print={printFlag}
      />

      
      <SelectFilterDialog
        open={openSelectFilterDialog}
        onClose={() => setOpenSelectFilterDialog(false)}
        screenName={screenNames.courses}
        onFilterChange={onFilterChange}
      />

      <SaveFilterDialog
        open={openSaveFilterDialog}
        onClose={() => setOpenSaveFilterDialog(false)}
        screenName={screenNames.courses}
        filters={localFilters}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  keyboard: state.keyboard,
  ...ownProps,
});

export default connect(mapStateToProps)(CourseTableToolbar);
